import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Units of Measure (PART 5, MD-12 — rewritten by owner ruling R6).
 *
 * The tenant's own unit VOCABULARY: names, symbols, decimals, and the two
 * allowed-on flags. NO factor travels through this contract — "how many
 * pieces a box holds" is a fact about the item and lives on the item's own
 * unit rows (MD-03 panel 5, itemsContract).
 *
 * Permission area: `inventory` — PART 16.2.2 row 3 ("Items, categories,
 * units of measure, warehouses, stock and movements…"). The 16.4.2 matrix:
 * Administrator RCUDA, Accountant R, AP Clerk R.
 */
// ── The fixed sentences of MD-12 ────────────────────────────────────────────
export const UNIT_MESSAGES = {
    /** ERR-MD-12-01 — the spec's example is a symbol clash. */
    symbolTaken: (symbol, name) => `${symbol} is already the symbol for ${name}.`,
    /** BR-MD-12-02's name half (no spec-fixed wording; modeled on ERR-MD-14-02). */
    nameTaken: (name) => `There is already a unit called ${name}.`,
    /** ERR-MD-12-05 (MD-RULE-1 / BR-MD-12-08, BR-MD-12-12). */
    inUse: (name, items) => `${name} is used by ${items} ${items === 1 ? 'item' : 'items'} and cannot be deleted. Switch it off instead.`,
    /** ERR-MD-12-07. */
    precisionRange: 'A unit allows between 0 and 3 decimals.',
    /**
     * BR-MD-12-11 / ERR-MD-12-06 — the spec's sentence for a whole-number
     * unit, generalized for units that allow decimals.
     */
    tooManyDecimals: (name, precision) => precision === 0
        ? `${name} are counted in whole numbers.`
        : `A quantity in ${name} allows ${precision} ${precision === 1 ? 'decimal' : 'decimals'}.`,
    /** BR-MD-12-09 (no spec-fixed wording; the shared inactive pattern). */
    inactive: (name) => `${name} is switched off and cannot be added to an item.`,
    /** MD-12 §6: Load starter units is hidden once any unit exists. */
    notEmpty: 'Starter units can only be loaded into an empty list.',
};
/**
 * D46 — the unit family's structured refusals (the d46masters wave): key +
 * params beside the composed English sentence, so a refusal that names a
 * unit, a symbol or an item count reaches an Arabic reader in Arabic. One
 * builder per parameterised member the API raises inside an HTTP exception
 * body — the raise sites throw THESE, and each `message` is composed by the
 * `UNIT_MESSAGES` twin so the two cannot drift (parity pinned member by
 * member in `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * `inUse` travels the RAW item count; the dictionaries pluralise the noun
 * (en mirrors the sentence's own one/other ternary, ar carries the full
 * six-category set). `tooManyDecimals` is the family's one FORKED sentence:
 * the twin says "counted in whole numbers" at precision 0 and "allows N
 * decimals" above it, so the dictionaries branch on the RAW `precision` —
 * an ICU plural with an exact `=0` arm, not a select token; the wire carries
 * the number, never a phrase. `inactive` and `tooManyDecimals` are raised by
 * the ITEMS service (adding a unit to an item is where both walls stand);
 * the other three are raised where units themselves are written.
 */
export const UNIT_REFUSALS = {
    symbolTaken: (symbol, name) => ({
        message: UNIT_MESSAGES.symbolTaken(symbol, name),
        key: 'units.errSymbolTaken',
        params: { symbol, name },
    }),
    nameTaken: (name) => ({
        message: UNIT_MESSAGES.nameTaken(name),
        key: 'units.errNameTaken',
        params: { name },
    }),
    inUse: (name, items) => ({
        message: UNIT_MESSAGES.inUse(name, items),
        key: 'units.errInUse',
        params: { name, items },
    }),
    tooManyDecimals: (name, precision) => ({
        message: UNIT_MESSAGES.tooManyDecimals(name, precision),
        key: 'units.errTooManyDecimals',
        params: { name, precision },
    }),
    inactive: (name) => ({
        message: UNIT_MESSAGES.inactive(name),
        key: 'units.errInactive',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const UnitSchema = z
    .object({
    id: z.uuid(),
    /** U-### — presentation of unitNo; never typed, never changes. */
    code: z.string(),
    unitNo: z.number().int().positive(),
    name: z.string(),
    /** S5 / UOM-01: optional Arabic name; the Arabic UI shows `nameAr ?? name`. */
    nameAr: z.string().nullable(),
    symbol: z.string(),
    /** S5 / UOM-01: optional Arabic abbreviation; the Arabic UI shows `symbolAr ?? symbol`. */
    symbolAr: z.string().nullable(),
    /** Field 7 — the OPTIONAL folder record (OQ-05-18); null = ungrouped. */
    groupId: z.uuid().nullable(),
    /** The folder's current name, joined for display (one record, BR-MD-12-06). */
    groupName: z.string().nullable(),
    /** 0..3 decimals a quantity in this unit may have (ERR-MD-12-07). */
    precision: z.number().int(),
    allowSales: z.boolean(),
    allowPurchases: z.boolean(),
    isActive: z.boolean(),
    /** MD-12 field 12: stock unit OR extra unit on any item — the delete pre-check. */
    itemsUsing: z.number().int(),
    // MD-RULE-5's six fields, `createdAt` among them. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
export const CreateUnitRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(60),
    nameAr: z.string().trim().min(1).max(60).optional(),
    symbol: z.string().trim().min(1).max(10),
    symbolAr: z.string().trim().min(1).max(10).optional(),
    /** Field 7 — a pick over the unit-groups folder list; omitted = unfiled. */
    groupId: z.uuid().optional(),
    precision: z
        .number()
        .int(UNIT_MESSAGES.precisionRange)
        .min(0, UNIT_MESSAGES.precisionRange)
        .max(3, UNIT_MESSAGES.precisionRange),
    allowSales: z.boolean().default(true),
    allowPurchases: z.boolean().default(true),
})
    .strict();
export const UpdateUnitRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(60).optional(),
    /** null clears the Arabic name (the Arabic UI falls back to `name`). */
    nameAr: z.string().trim().min(1).max(60).nullable().optional(),
    symbol: z.string().trim().min(1).max(10).optional(),
    symbolAr: z.string().trim().min(1).max(10).nullable().optional(),
    /** BR-MD-12-06: changeable at ANY time; null refiles to ungrouped. */
    groupId: z.uuid().nullable().optional(),
    precision: z
        .number()
        .int(UNIT_MESSAGES.precisionRange)
        .min(0, UNIT_MESSAGES.precisionRange)
        .max(3, UNIT_MESSAGES.precisionRange)
        .optional(),
    allowSales: z.boolean().optional(),
    allowPurchases: z.boolean().optional(),
})
    .strict();
const unitParams = z.object({ unitId: z.uuid() }).strict();
export const unitsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/units',
        query: z
            .object({
            q: z.string().optional(),
            /** BR-MD-12-09: pickers exclude the switched-off by default. */
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ units: UnitSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The unit vocabulary (inventory.view). No factor column — R6.',
    },
    get: {
        method: 'GET',
        path: '/api/units/:unitId',
        pathParams: unitParams,
        responses: { 200: UnitSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'One unit (inventory.view).',
    },
    create: {
        method: 'POST',
        path: '/api/units',
        body: CreateUnitRequestSchema,
        responses: {
            201: UnitSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New unit (inventory.create). Duplicate name or symbol refused (BR-MD-12-02 / ERR-MD-12-01).',
    },
    update: {
        method: 'PATCH',
        path: '/api/units/:unitId',
        pathParams: unitParams,
        body: UpdateUnitRequestSchema,
        responses: {
            200: UnitSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a unit (inventory.edit). Renaming renames it everywhere at once — nothing stores the text (BR-MD-12-14).',
    },
    deactivate: {
        method: 'POST',
        path: '/api/units/:unitId/deactivate',
        pathParams: unitParams,
        body: z.object({}).strict().optional(),
        responses: { 200: UnitSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Switch off (inventory.edit). Items already using it keep working and keep converting.',
    },
    reactivate: {
        method: 'POST',
        path: '/api/units/:unitId/reactivate',
        pathParams: unitParams,
        body: z.object({}).strict().optional(),
        responses: { 200: UnitSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Switch back on (inventory.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/units/:unitId',
        pathParams: unitParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (inventory.delete) — only a unit used by NO item (BR-MD-12-08, BR-MD-12-12, ERR-MD-12-05).',
    },
    seedStarter: {
        method: 'POST',
        path: '/api/units/starter-set',
        body: z.object({}).strict().optional(),
        responses: {
            201: z.object({ units: UnitSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Load starter units (inventory.create) — MD-12 §6, only while the list is empty. The set is an EXAMPLE (BR-MD-12-15).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=units.js.map